import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { ProductCard } from "@/components/ProductCard";

export default function Shop() {
  const { shopCategories: categories } = useSettings();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const activeCat = params.get("category") || "";

  useEffect(() => {
    setLoading(true);
    api.get("/products", { params: activeCat ? { category: activeCat } : {} })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [activeCat]);

  const sizes = useMemo(() => [...new Set(products.flatMap((p) => p.sizes || []))], [products]);
  const [size, setSize] = useState("");
  const shown = size ? products.filter((p) => (p.sizes || []).includes(size)) : products;

  const setCat = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set("category", id); else next.delete("category");
    setParams(next);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="border-b border-kys-blush-border pb-6">
        <p className="label-accent text-kys-accent">Ready to Ship</p>
        <h1 className="mt-2 font-serif text-4xl tracking-tight text-kys-ink sm:text-5xl">Shop the Collection</h1>
        <p className="mt-2 max-w-xl text-sm text-kys-charcoal">Handmade pieces ready to ship now. Many are one-of-a-kind — once it's gone, it's gone.</p>
      </div>

      {/* Filters */}
      <div className="sticky top-16 z-30 -mx-4 mt-4 bg-kys-cream/90 px-4 py-3 backdrop-blur sm:mx-0 sm:rounded-md sm:px-0">
        <div className="no-scrollbar flex gap-2 overflow-x-auto">
          <button data-testid="filter-cat-all" onClick={() => setCat("")}
            className={`whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition ${!activeCat ? "border-kys-ink bg-kys-ink text-white" : "border-kys-blush-border bg-white text-kys-charcoal hover:border-kys-accent"}`}>
            All
          </button>
          {categories.map((c) => (
            <button key={c.id} data-testid={`filter-cat-${c.slug}`} onClick={() => setCat(c.id)}
              className={`whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition ${activeCat === c.id ? "border-kys-ink bg-kys-ink text-white" : "border-kys-blush-border bg-white text-kys-charcoal hover:border-kys-accent"}`}>
              {c.name}
            </button>
          ))}
        </div>
        {sizes.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-xs text-kys-muted">Size:</span>
            <button onClick={() => setSize("")} className={`rounded border px-2.5 py-1 text-xs ${!size ? "border-kys-accent text-kys-accent" : "border-kys-blush-border text-kys-charcoal"}`}>All</button>
            {sizes.map((s) => (
              <button key={s} onClick={() => setSize(s)} className={`rounded border px-2.5 py-1 text-xs ${size === s ? "border-kys-accent text-kys-accent" : "border-kys-blush-border text-kys-charcoal"}`}>{s}</button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-8">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] animate-pulse rounded-md bg-kys-blush-soft" />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <div data-testid="shop-empty" className="mt-16 rounded-md border border-dashed border-kys-blush-border bg-white py-20 text-center">
          <p className="font-serif text-2xl text-kys-ink">No pieces here yet</p>
          <p className="mt-2 text-sm text-kys-muted">Check back soon — new handmade pieces are added regularly.</p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-8">
          {shown.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
